import type { FC } from 'react';
import type { Control, FieldPath } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormMultiSelect } from '../FormMultiSelect/FormMultiSelect';
import { FormSelect } from '../FormSelect/FormSelect';
import { FormTextField } from '../FormTextField/FormTextField';
import {
  FIELD_WIDTH_NARROW,
  FIELD_WIDTH_THRESHOLD,
  FIELD_WIDTH_WIDE,
} from './cronConstants';
import {
  dayOptions,
  everyOptions,
  monthOptions,
  timezoneOptions,
  weekDayOptions,
} from './cronConstants';
import type { CronEveryOption, CronFormData } from './cronTypes';

type FieldProps = {
  everyValue: CronEveryOption;
  disabled?: boolean;
};

const useCronControl = (): Control<CronFormData> => {
  const { control } = useFormContext<CronFormData>();
  return control;
};

/**
 * Hook to determine the width class for custom cron input fields based on field value length
 */
function useCustomFieldWidth(fieldName: FieldPath<CronFormData>): string {
  const control = useCronControl();
  const fieldValue = useWatch({
    control,
    name: fieldName,
  }) as string | undefined;
  const fieldLength = fieldValue?.length ?? 0;
  return fieldLength > FIELD_WIDTH_THRESHOLD
    ? FIELD_WIDTH_WIDE
    : FIELD_WIDTH_NARROW;
}

type EveryFieldProps = {
  disabled?: boolean;
};

export const EveryField: FC<EveryFieldProps> = ({ disabled = false }) => {
  const control = useCronControl();
  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium'>Every</span>
      <div className='w-32'>
        <FormSelect.Mandatory
          control={control}
          name='every'
          options={everyOptions}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const MonthsField: FC<FieldProps> = ({
  everyValue,
  disabled = false,
}) => {
  const control = useCronControl();
  const isCustom = everyValue === 'custom';
  const widthClass = useCustomFieldWidth('months');

  if (isCustom) {
    return (
      <div className='flex flex-col items-center gap-1'>
        <div className={widthClass}>
          <FormTextField
            control={control as Control<CronFormData & { months: string }>}
            name='months'
            placeholder='*'
            type='text'
            disabled={disabled}
          />
        </div>
        <span className='text-xs'>month</span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium flex-[0_0_1rem]'>in</span>
      <div className='w-60 flex-[1_0_auto]'>
        <FormMultiSelect
          control={control as Control<CronFormData & { months: string[] }>}
          name='months'
          options={monthOptions}
          placeholder='Choose month(s)'
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const DaysField: FC<FieldProps> = ({ everyValue, disabled = false }) => {
  const control = useCronControl();
  const isCustom = everyValue === 'custom';
  const widthClass = useCustomFieldWidth('days');

  if (isCustom) {
    return (
      <div className='flex flex-col items-center gap-1'>
        <div className={widthClass}>
          <FormTextField
            control={control as Control<CronFormData & { days: string }>}
            name='days'
            placeholder='*'
            type='text'
            disabled={disabled}
          />
        </div>
        <span className='text-xs'>day</span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium flex-[0_0_1rem]'>on</span>
      <div className='w-60 flex-[1_0_auto]'>
        <FormMultiSelect
          control={control as Control<CronFormData & { days: string[] }>}
          name='days'
          options={dayOptions}
          placeholder='Choose day(s)'
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export const WeekDaysField: FC<FieldProps> = ({
  everyValue,
  disabled = false,
}) => {
  const control = useCronControl();
  const isCustom = everyValue === 'custom';
  const widthClass = useCustomFieldWidth('weekDays');

  if (isCustom) {
    const weekDayLabel = (
      <div className='flex flex-col items-center gap-0.51'>
        <span>week</span>
        <span>day</span>
      </div>
    );
    return (
      <div className='flex flex-col items-center gap-1'>
        <div className={widthClass}>
          <FormTextField
            control={control as Control<CronFormData & { weekDays: string }>}
            name='weekDays'
            placeholder='*'
            type='text'
            disabled={disabled}
          />
        </div>
        <span className='text-xs'>{weekDayLabel}</span>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium flex-[0_0_1rem]'>on</span>
      <div className='w-60 flex-[1_0_auto]'>
        <FormMultiSelect
          control={control as Control<CronFormData & { weekDays: string[] }>}
          name='weekDays'
          options={weekDayOptions}
          placeholder='Choose day(s)'
          disabled={disabled}
        />
      </div>
    </div>
  );
};

type DisabledFieldProps = {
  disabled?: boolean;
};

export const HourField: FC<DisabledFieldProps> = ({ disabled = false }) => {
  const control = useCronControl();
  const widthClass = useCustomFieldWidth('hour');
  return (
    <div className='flex flex-col items-center gap-1'>
      <div className={widthClass}>
        <FormTextField
          control={control}
          name='hour'
          type='text'
          disabled={disabled}
        />
      </div>
      <span className='text-xs'>hr</span>
    </div>
  );
};

export const MinuteField: FC<DisabledFieldProps> = ({ disabled = false }) => {
  const control = useCronControl();
  const widthClass = useCustomFieldWidth('minute');
  return (
    <div className='flex flex-col items-center gap-1'>
      <div className={widthClass}>
        <FormTextField
          control={control}
          name='minute'
          type='text'
          disabled={disabled}
        />
      </div>
      <span className='text-xs'>min</span>
    </div>
  );
};

export const TimezoneField: FC<DisabledFieldProps> = ({ disabled = false }) => {
  const control = useCronControl();
  return (
    <div className='flex flex-col items-center gap-1'>
      <div className='w-64'>
        <FormSelect.Mandatory
          control={control}
          name='timezone'
          options={timezoneOptions}
          disabled={disabled}
        />
      </div>
      <span className='text-xs'>timezone</span>
    </div>
  );
};

export const TimezoneInlineField: FC<DisabledFieldProps> = ({
  disabled = false,
}) => {
  const control = useCronControl();
  return (
    <div className='w-64'>
      <FormSelect.Mandatory
        control={control}
        name='timezone'
        options={timezoneOptions}
        disabled={disabled}
      />
    </div>
  );
};
